import type { RequestContext } from "../context/request-context.js";
import type { CalculationRepository } from "../../infrastructure/database/memory/in-memory-calculation-repository.js";
import type { EquipmentRepository } from "../../equipment/equipment-domain.js";
import type { MeReportRepository } from "../../repositories/operational-repositories.js";
import type { IntegrityCatalogService, AsmeNormativeParameters } from "./integrity-catalog-service.js";
import {
  CALCULATION_NORMS,
  formatCalculationCode,
  type CalculationKind,
  type CalculationRecord
} from "../../domain/engineering/calculation-record.js";
import { calculateApi570, type Api570Inputs, type Api570Result } from "../../domain/engineering/api570-calculator.js";
import { calculateNaceCoupon, type NaceCouponInputs, type NaceResult } from "../../domain/engineering/nace-calculator.js";
import { calculateAsmeB313, type AsmeB313Inputs, type AsmeB313Result } from "../../domain/engineering/asme-b313-calculator.js";
import { extractLineDesignData, type LineDesignData } from "../../domain/engineering/line-design-data.js";
import {
  buildCmlThicknessHistory,
  listCmlsForEquipment,
  type CmlSummary,
  type CmlThicknessHistory
} from "../../domain/engineering/me-thickness-history.js";
import type { PipingMaterial } from "../../domain/engineering/integrity-catalog.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

const randomUUID = () => globalThis.crypto.randomUUID();

export interface CalculationContextInfo {
  equipmentId?: string;
  equipmentTag?: string;
  notes?: string;
  /** Edição normativa efetivamente adotada neste cálculo. */
  editionId?: string;
  editionLabel?: string;
  /** Origem de cada parâmetro (cadastro, tabela normativa, entrada manual justificada). */
  provenance?: Record<string, string>;
}

/** Linha/tubulação selecionável nas calculadoras. Fonte única: o cadastro de equipamentos. */
export interface CalculableLine {
  equipmentId: string;
  tag: string;
  name: string;
  equipmentType: string;
  nps: string | null;
  material: string | null;
  /** Serviço/fluido, para compor "TAG — DESCRIÇÃO" no seletor. */
  service: string | null;
  clientId: string;
  projectId: string | null;
  status: string;
}

export interface CalculableLineFilters {
  clientId?: string;
  projectId?: string;
}

/** Tudo que a tela precisa depois de escolher a TAG — sem nenhuma redigitação. */
export interface LineCalculationContext {
  design: LineDesignData;
  matchedMaterial: PipingMaterial | null;
  normative: AsmeNormativeParameters | null;
}

export interface Api570PointContext {
  history: CmlThicknessHistory;
  /** Último cálculo ASME B31.3 da TAG, quando existir. */
  minimumThicknessFromAsme: { value: number; sourceCode: string; calculatedAt: string } | null;
}

/**
 * Três calculadoras independentes atrás de um serviço: API 570, NACE/AMPP e
 * ASME B31.3. Cada método roda apenas a sua fórmula, grava apenas o seu
 * registro e numera-se na sua própria sequência.
 *
 * Não existe método que rode mais de uma delas, e o resultado de uma nunca
 * alimenta outra aqui — um valor só atravessa por ação explícita do usuário
 * (ver transferMinimumThicknessToApi570).
 */
export class EngineeringCalculationService {
  constructor(
    private readonly repo: CalculationRepository,
    private readonly equipmentRepo?: EquipmentRepository,
    private readonly meReportRepo?: MeReportRepository,
    private readonly catalogs?: IntegrityCatalogService
  ) {}

  // =========================================================================
  // Dados que o sistema busca sozinho (cadastro da linha, histórico de ME)
  // =========================================================================

  /**
   * Linhas/tubulações reais do cadastro — não é uma lista paralela. Equipamentos
   * que não são tubulação por tipo, mas têm dados de linha preenchidos
   * (NPS, schedule, nº da linha), entram também: foi assim que muitos
   * registros antigos foram cadastrados.
   */
  async listCalculableLines(
    context: RequestContext,
    filters: CalculableLineFilters = {}
  ): Promise<CalculableLine[]> {
    this.requireAccess(context);
    if (!this.equipmentRepo) return [];

    // Sem cliente escolhido a calculadora NÃO pode ficar vazia: o repositório
    // devolve todas as linhas quando nenhum cliente é informado, e o usuário
    // filtra por Cliente/Projeto dentro da própria calculadora.
    const clientId = filters.clientId || context.activeClientId || undefined;
    const equipments = await this.equipmentRepo.listEquipments(clientId as any);

    return equipments
      .filter(eq => !filters.projectId || eq.projectId === filters.projectId)
      // Linha inativa não é oferecida para um cálculo novo; cálculos já
      // gravados continuam mostrando a TAG no próprio histórico.
      .filter(eq => eq.status !== "INACTIVE")
      // Só o que é tubulação pelo domínio real (PIPING_LINE / PIPING_SYSTEM),
      // mais registros antigos que já trazem dados de linha preenchidos.
      .filter(eq =>
        eq.equipmentType === "PIPING_LINE" ||
        eq.equipmentType === "PIPING_SYSTEM" ||
        Boolean(eq.nps) ||
        Boolean(eq.schedule) ||
        Boolean(eq.lineNumber)
      )
      .map(eq => ({
        equipmentId: String(eq.id),
        tag: eq.tag,
        name: eq.name,
        equipmentType: eq.equipmentType,
        nps: eq.nps ?? null,
        material: eq.material ?? null,
        service: eq.service ?? eq.fluid ?? null,
        clientId: String(eq.clientId),
        projectId: eq.projectId ?? null,
        status: eq.status
      }))
      .sort((a, b) => a.tag.localeCompare(b.tag, "pt-BR", { numeric: true }));
  }

  /**
   * Dados de projeto da linha + material do catálogo + S/E/W/Y.
   *
   * `temperatureOverrideCelsius` roda um CENÁRIO de cálculo numa temperatura
   * diferente da cadastrada: busca o S correspondente, sem tocar no cadastro.
   */
  async loadLineContext(
    context: RequestContext,
    equipmentId: string,
    options: { editionId?: string; materialId?: string | null; temperatureOverrideCelsius?: number | null } = {}
  ): Promise<LineCalculationContext> {
    this.requireAccess(context);

    if (!this.equipmentRepo) {
      throw new AppError(ErrorCode.NotFound, "Equipment repository not wired into the calculation service", {
        safeMessage: "Cadastro de equipamentos indisponível nesta sessão."
      });
    }

    // A linha é buscada pelo seu id. Quando não há cliente ativo, o cliente da
    // própria linha é quem vale — o cadastro não deixa de existir por não
    // haver contexto global selecionado.
    const equipment =
      (await this.equipmentRepo.getEquipment(context.activeClientId as any, equipmentId as any)) ??
      (await this.equipmentRepo.getEquipment(undefined as any, equipmentId as any));

    if (!equipment) {
      throw new AppError(ErrorCode.NotFound, `Equipment ${equipmentId} not found`, {
        safeMessage: "A linha selecionada não foi encontrada no cadastro."
      });
    }

    const design = extractLineDesignData(equipment);

    if (!this.catalogs) {
      return { design, matchedMaterial: null, normative: null };
    }

    const matched = options.materialId
      ? (await this.catalogs.listMaterials(context)).find(m => m.id === options.materialId) ?? null
      : await this.catalogs.matchMaterial(context, design.material, design.materialSpecification, design.materialGrade);

    const edition =
      options.editionId ?? (await this.catalogs.defaultEdition(context, "ASME B31.3"))?.id ?? "";

    const temperature =
      options.temperatureOverrideCelsius !== undefined && options.temperatureOverrideCelsius !== null
        ? options.temperatureOverrideCelsius
        : design.designTemperatureCelsius.value;

    const normative = await this.catalogs.resolveAsmeParameters(context, {
      materialId: matched?.id ?? null,
      temperatureCelsius: temperature,
      editionId: edition
    });

    return { design, matchedMaterial: matched, normative };
  }

  /** Pontos (CML) já medidos no equipamento, vindos dos relatórios de ME reais. */
  async listMeasurementPoints(context: RequestContext, equipmentId: string): Promise<CmlSummary[]> {
    this.requireAccess(context);
    const reports = await this.loadMeReports(context, equipmentId);
    return listCmlsForEquipment(reports, equipmentId);
  }

  /**
   * Histórico de espessura do ponto + espessura mínima requerida do último
   * cálculo ASME B31.3 da mesma TAG. Nada disso é digitado pelo usuário.
   */
  async loadPointContext(context: RequestContext, equipmentId: string, cmlId: string): Promise<Api570PointContext> {
    this.requireAccess(context);
    const reports = await this.loadMeReports(context, equipmentId);
    const history = buildCmlThicknessHistory(reports, equipmentId, cmlId);
    const latestAsme = await this.findLatestAsmeForEquipment(context, equipmentId);
    return { history, minimumThicknessFromAsme: latestAsme };
  }

  /** Último cálculo ASME B31.3 registrado para a TAG, se houver. */
  async findLatestAsmeForEquipment(
    context: RequestContext,
    equipmentId: string
  ): Promise<{ value: number; sourceCode: string; calculatedAt: string } | null> {
    this.requireAccess(context);

    const records = await this.repo.listByKind("ASME_B313");
    const forLine = records.filter(r => r.equipmentId === equipmentId);
    const latest = forLine[0]; // listByKind já ordena do mais recente para o mais antigo
    if (!latest) return null;

    const value = (latest.result as any)?.minimumRequiredThickness;
    if (typeof value !== "number" || !Number.isFinite(value)) return null;

    return { value, sourceCode: latest.code, calculatedAt: latest.performedAt };
  }

  /**
   * Relatórios de ME do cliente da linha. Sem cliente ativo, o cliente vem do
   * próprio equipamento — o histórico de medição não some por falta de
   * contexto global.
   */
  private async loadMeReports(context: RequestContext, equipmentId?: string) {
    if (!this.meReportRepo) return [];

    let clientId: string | null = context.activeClientId ?? null;
    if (!clientId && equipmentId && this.equipmentRepo) {
      const equipment = await this.equipmentRepo.getEquipment(undefined as any, equipmentId as any);
      clientId = equipment ? String(equipment.clientId) : null;
    }

    if (clientId) return this.meReportRepo.listByClient(context, clientId);
    return this.meReportRepo.listAll ? this.meReportRepo.listAll() : [];
  }

  // =========================================================================
  // CALCULADORA 01 — API 570
  // =========================================================================

  /** Executa a fórmula sem gravar — é o que a tela usa enquanto o usuário confere os dados. */
  previewApi570(context: RequestContext, inputs: Api570Inputs): Api570Result {
    this.requireAccess(context);
    return calculateApi570(inputs);
  }

  async runApi570(
    context: RequestContext,
    inputs: Api570Inputs,
    info: CalculationContextInfo = {}
  ): Promise<{ result: Api570Result; record: CalculationRecord }> {
    this.requireAccess(context);
    const result = calculateApi570(inputs);
    const record = await this.persist(context, "API570", inputs as any, result as any, {
      thickness: "mm",
      corrosionRate: "mm/ano",
      remainingLife: "anos"
    }, info);
    return { result, record };
  }

  // =========================================================================
  // CALCULADORA 02 — NACE / AMPP
  // =========================================================================

  previewNace(context: RequestContext, inputs: NaceCouponInputs): NaceResult {
    this.requireAccess(context);
    return calculateNaceCoupon(inputs);
  }

  async runNace(
    context: RequestContext,
    inputs: NaceCouponInputs,
    info: CalculationContextInfo = {}
  ): Promise<{ result: NaceResult; record: CalculationRecord }> {
    this.requireAccess(context);
    const result = calculateNaceCoupon(inputs);
    const record = await this.persist(context, "NACE", inputs as any, result as any, {
      mass: "g",
      length: "mm",
      area: "mm²",
      time: "dias",
      density: "g/cm³",
      corrosionRate: "mm/ano"
    }, info);
    return { result, record };
  }

  // =========================================================================
  // CALCULADORA 03 — ASME B31.3
  // =========================================================================

  previewAsmeB313(context: RequestContext, inputs: AsmeB313Inputs): AsmeB313Result {
    this.requireAccess(context);
    return calculateAsmeB313(inputs);
  }

  async runAsmeB313(
    context: RequestContext,
    inputs: AsmeB313Inputs,
    info: CalculationContextInfo = {}
  ): Promise<{ result: AsmeB313Result; record: CalculationRecord }> {
    this.requireAccess(context);
    const result = calculateAsmeB313(inputs);
    const record = await this.persist(context, "ASME_B313", inputs as any, result as any, {
      pressure: inputs.pressureUnit || "MPa",
      length: inputs.lengthUnit || "mm",
      thickness: inputs.lengthUnit || "mm",
      temperature: "°C",
      tolerance: "%"
    }, info);
    return { result, record };
  }

  // =========================================================================
  // Histórico independente por tipo
  // =========================================================================

  async listHistory(context: RequestContext, kind: CalculationKind): Promise<CalculationRecord[]> {
    this.requireAccess(context);
    return this.repo.listByKind(kind);
  }

  /**
   * Reaproveitamento explícito e rastreável: pega a espessura mínima requerida
   * de um cálculo ASME B31.3 registrado para usar como entrada do API 570.
   * Nada acontece sozinho — quem chama pede por nome, e o código do cálculo de
   * origem volta junto para o reaproveitamento continuar auditável.
   */
  async transferMinimumThicknessToApi570(
    context: RequestContext,
    asmeCalculationId: string
  ): Promise<{ minimumRequiredThicknessMm: number; sourceCode: string }> {
    this.requireAccess(context);

    const record = await this.repo.findById(asmeCalculationId);
    if (!record || record.kind !== "ASME_B313") {
      throw new AppError(ErrorCode.NotFound, "ASME B31.3 calculation not found for reuse", {
        safeMessage: "Cálculo ASME B31.3 não encontrado para reaproveitamento."
      });
    }

    const value = (record.result as any)?.minimumRequiredThickness;
    if (typeof value !== "number" || !Number.isFinite(value)) {
      throw new AppError(ErrorCode.Validation, "ASME B31.3 calculation has no minimum required thickness", {
        safeMessage: "O cálculo ASME B31.3 selecionado não possui espessura mínima requerida."
      });
    }

    return { minimumRequiredThicknessMm: value, sourceCode: record.code };
  }

  private requireAccess(context: RequestContext): void {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.CalculationAccess);
  }

  private async persist(
    context: RequestContext,
    kind: CalculationKind,
    inputs: Record<string, unknown>,
    result: Record<string, unknown>,
    units: Record<string, string>,
    info: CalculationContextInfo
  ): Promise<CalculationRecord> {
    const sequence = (await this.repo.countByKind(kind)) + 1;
    const norm = CALCULATION_NORMS[kind];

    const record: CalculationRecord = {
      id: randomUUID(),
      code: formatCalculationCode(kind, sequence),
      kind,
      norm: norm.norm,
      // A edição adotada fica gravada no cálculo; sem escolha explícita vale a
      // referência normativa padrão do tipo.
      normEdition: info.editionLabel || norm.edition,
      editionId: info.editionId || null,
      performedAt: new Date().toISOString(),
      performedByUserId: context.userId || "system",
      performedByName: (context as any).userName || context.userId || "system",
      clientId: context.activeClientId || null,
      equipmentId: info.equipmentId || null,
      equipmentTag: info.equipmentTag || "",
      notes: info.notes || "",
      provenance: info.provenance || {},
      inputs,
      result,
      units
    };

    await this.repo.save(record);
    return record;
  }
}
